import type { AxiosInstance, AxiosRequestConfig } from '@deltax/idp-api-client';
import { createApiClient } from '@deltax/idp-api-client';
import { handleError } from '@/api/errorHandler';
import { useAppStore } from '@/stores/app';

const { apiClient: baseApiClient, authService: packageAuthService } =
  createApiClient({
    clientId: (import.meta.env.VITE_APP_DELTAX_CLIENT_APP_ID as string) || '',
    resourceApiBaseUrl: (import.meta.env.VITE_API_BASE_URL as string) || '',
    idpBaseUrl: (import.meta.env.VITE_IDP_BASE_URL as string) || '',
    errorHandler: handleError
  });

const { apiClient: stunnrAiBaseApiClient } = createApiClient({
  clientId: (import.meta.env.VITE_APP_DELTAX_CLIENT_APP_ID as string) || '',
  resourceApiBaseUrl:
    (import.meta.env.VITE_STUNNR_AI_API_BASE_URL as string) || '',
  idpBaseUrl: (import.meta.env.VITE_IDP_BASE_URL as string) || '',
  errorHandler: handleError
});

const { apiClient: idpBaseApiClient } = createApiClient({
  clientId: (import.meta.env.VITE_APP_DELTAX_CLIENT_APP_ID as string) || '',
  resourceApiBaseUrl: (import.meta.env.VITE_IDP_BASE_URL as string) || '',
  idpBaseUrl: (import.meta.env.VITE_IDP_BASE_URL as string) || '',
  errorHandler: handleError
});

export const axiosInstance = baseApiClient.axiosInstance;
export const oAuth2AxiosInstance = baseApiClient.oAuth2AxiosInstance;
export { packageAuthService };

/**
 * ApiClient
 */
class ApiClient {
  protected readonly axiosInstance: AxiosInstance;
  protected readonly oAuth2AxiosInstance: AxiosInstance;

  constructor(
    customAxiosInstance?: AxiosInstance,
    customOAuth2AxiosInstance?: AxiosInstance
  ) {
    this.axiosInstance = customAxiosInstance || axiosInstance;
    this.oAuth2AxiosInstance = customOAuth2AxiosInstance || oAuth2AxiosInstance;
  }

  // Public method for GET requests
  public async get<T>(
    url: string,
    params?: AxiosRequestConfig['params'],
    config: AxiosRequestConfig = {},
    useOAuth2 = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2);
    const response = await instance.get<T>(url, { params, ...config });
    return response.data;
  }

  // Public method for POST requests
  public async post<T>(
    url: string,
    data: unknown = undefined,
    config: AxiosRequestConfig = {},
    useOAuth2 = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2);
    const response = await instance.post<T>(url, data, config);
    return response.data;
  }

  // Public method for PATCH requests
  public async patch<T>(
    url: string,
    data: unknown,
    config: AxiosRequestConfig = {},
    useOAuth2 = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2);
    const response = await instance.patch<T>(url, data, config);
    return response.data;
  }

  // Public method for DELETE requests
  public async delete<T>(
    url: string,
    config: AxiosRequestConfig = {},
    useOAuth2 = false
  ): Promise<T> {
    const instance = this.getAxiosInstance(useOAuth2);
    const response = await instance.delete<T>(url, config);
    return response.data;
  }

  // Private method to decide which Axios instance to use
  private getAxiosInstance(useOAuth2: boolean): AxiosInstance {
    return useOAuth2 ? this.oAuth2AxiosInstance : this.axiosInstance;
  }
}

const bootstrapApiClient = async () => {
  const appStore = useAppStore();
  if (!appStore.portalData) {
    await appStore.fetchPortalData();
  }
};

const apiClient = new ApiClient();

/** Client for the Stunnr AI (adgen) API — separate host, same auth flow. */
const stunnrAiApiClient = new ApiClient(
  stunnrAiBaseApiClient.axiosInstance,
  stunnrAiBaseApiClient.oAuth2AxiosInstance
);

/** Client for DeltaX IDP endpoints directly */
const idpApiClient = new ApiClient(
  idpBaseApiClient.axiosInstance,
  idpBaseApiClient.oAuth2AxiosInstance
);

export { ApiClient, bootstrapApiClient, stunnrAiApiClient, idpApiClient };
export default apiClient;
